import { http } from '../lib/api/http'

const USER_SESSIONS_ENDPOINT = '/user-sessions'
const DEFAULT_LIMIT = 20
const SESSION_RECORD_KEY_PREFIX = 'flotte:session-record:'
const SESSION_TOKEN_STORAGE_KEY = 'flotte:session-token'
const GEOLOCATION_ENDPOINT = 'https://ipapi.co/json/'

const isBrowser = typeof window !== 'undefined'
let geoLocationCache = null
let geoLocationPromise = null

const safeStorage = {
  get(key) {
    if (!isBrowser || !key) return ''
    try {
      return window.localStorage.getItem(key) || ''
    } catch {
      return ''
    }
  },
  set(key, value) {
    if (!isBrowser || !key) return
    try {
      window.localStorage.setItem(key, value)
    } catch {
      /* noop */
    }
  },
  remove(key) {
    if (!isBrowser || !key) return
    try {
      window.localStorage.removeItem(key)
    } catch {
      /* noop */
    }
  },
}

const randomSessionToken = () => {
  if (typeof crypto !== 'undefined') {
    if (crypto.randomUUID) {
      return crypto.randomUUID().replace(/-/g, '')
    }
    const array = new Uint8Array(16)
    crypto.getRandomValues(array)
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('')
  }
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`
}

const getSessionToken = () => {
  let token = safeStorage.get(SESSION_TOKEN_STORAGE_KEY)
  if (!token) {
    token = randomSessionToken()
    safeStorage.set(SESSION_TOKEN_STORAGE_KEY, token)
  }
  return token
}

const getRecordStorageKey = userId => {
  if (!userId) return ''
  const token = getSessionToken()
  return `${SESSION_RECORD_KEY_PREFIX}${userId}:${token}`
}

const getStoredRecordId = userId => {
  const key = getRecordStorageKey(userId)
  if (!key) return ''
  return safeStorage.get(key)
}

const storeRecordId = (userId, recordId) => {
  if (!recordId) return
  const key = getRecordStorageKey(userId)
  if (!key) return
  safeStorage.set(key, recordId)
}

const clearStoredRecordId = userId => {
  const key = getRecordStorageKey(userId)
  if (!key) return
  safeStorage.remove(key)
}

const resolveGeoInfo = async () => {
  if (!isBrowser || typeof fetch === 'undefined') {
    return geoLocationCache
  }
  if (geoLocationCache) {
    return geoLocationCache
  }
  if (geoLocationPromise) {
    return geoLocationPromise
  }

  geoLocationPromise = fetch(GEOLOCATION_ENDPOINT, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
    },
  })
    .then(async response => {
      if (!response.ok) return null
      const data = await response.json().catch(() => null)
      if (!data) return null
      return {
        ip: data.ip || '',
        city: data.city || '',
        region: data.region || data.region_name || '',
        country: data.country_name || data.country || '',
      }
    })
    .catch(() => null)
    .finally(() => {
      geoLocationPromise = null
    })

  const result = await geoLocationPromise
  if (result) {
    geoLocationCache = result
  }
  return result
}

const buildLocationLabel = record => {
  const city = record?.city || record?.locationCity || record?.cityName
  const region = record?.region || record?.state || record?.locationState
  const country = record?.country || record?.countryCode || record?.locationCountry
  return [city, region, country].filter(Boolean).join(', ')
}

const resolveLocalRecordMatch = (recordId, localRecordId) => {
  if (!recordId || !localRecordId) return false
  return recordId === localRecordId
}

export const mapSessionRecord = (record, localRecordId = '') => {
  if (!record) {
    return {
      id: '',
      device: 'Dispositivo',
      location: '',
      lastActive: '',
      isCurrent: false,
      ipAddress: '',
      platform: '',
      browser: '',
    }
  }

  const location = record.location_label || record.locationLabel || buildLocationLabel(record)
  const lastActive =
    record.last_active ||
    record.lastActive ||
    record.lastSeenAt ||
    record.lastSeen ||
    record.updated ||
    record.created ||
    ''

  return {
    id: record.id,
    device:
      record.deviceLabel ||
      record.device ||
      record.deviceName ||
      record.platform ||
      'Dispositivo sin nombre',
    location: location || 'Ubicación desconocida',
    lastActive,
    isCurrent:
      typeof record.is_current === 'boolean'
        ? record.is_current
        : resolveLocalRecordMatch(record.id, localRecordId),
    ipAddress:
      record.ip_address ||
      record.ipAddress ||
      record.ip ||
      record.ipv4 ||
      record.ipv6 ||
      '',
    platform: record.platform || record.os || record.system || '',
    browser: record.browser || record.browserName || record.userAgentLabel || '',
  }
}

export async function fetchUserSessions({ userId, limit = DEFAULT_LIMIT } = {}) {
  if (!userId) return []
  try {
    const localRecordId = getStoredRecordId(userId)
    const params = userId ? { user_id: userId } : undefined
    const { data } = await http.get(USER_SESSIONS_ENDPOINT, { params })
    const items = Array.isArray(data) ? data : []
    return items.slice(0, limit).map(record => mapSessionRecord(record, localRecordId))
  } catch (error) {
    console.warn('No se pudieron cargar las sesiones activas.', error)
    throw error
  }
}

export async function revokeUserSession(sessionId) {
  if (!sessionId) return false
  try {
    await http.delete(`${USER_SESSIONS_ENDPOINT}/${encodeURIComponent(sessionId)}`)
    return true
  } catch (error) {
    console.warn('No se pudo cerrar la sesión solicitada.', error)
    throw error
  }
}

const getNavigatorInfo = () => {
  if (typeof navigator === 'undefined') {
    return {
      userAgent: 'unknown',
      platform: 'unknown',
    }
  }
  return {
    userAgent: navigator.userAgent || 'unknown',
    platform: navigator.platform || navigator.userAgentData?.platform || 'browser',
  }
}

const detectDeviceLabel = userAgent => {
  const ua = (userAgent || '').toLowerCase()
  if (ua.includes('iphone') || ua.includes('ipad')) return 'iOS · Web'
  if (ua.includes('android')) return 'Android · Web'
  if (ua.includes('mac')) return 'Mac · Web'
  if (ua.includes('win')) return 'Windows · Web'
  if (ua.includes('linux')) return 'Linux · Web'
  return 'Navegador web'
}

const detectBrowserName = userAgent => {
  const ua = userAgent || ''
  if (/edg/i.test(ua)) return 'Edge'
  if (/chrome|crios/i.test(ua) && !/edge|edg/i.test(ua)) return 'Chrome'
  if (/safari/i.test(ua) && !/chrome|crios|android/i.test(ua)) return 'Safari'
  if (/firefox|fxios/i.test(ua)) return 'Firefox'
  if (/opr|opera/i.test(ua)) return 'Opera'
  return 'Navegador'
}

const buildSessionPayload = (geoInfo) => {
  const { userAgent, platform } = getNavigatorInfo()
  const locationParts = []
  if (geoInfo?.city) locationParts.push(geoInfo.city)
  if (geoInfo?.region && geoInfo.region !== geoInfo.city) locationParts.push(geoInfo.region)
  if (geoInfo?.country) locationParts.push(geoInfo.country)
  const locationLabel = locationParts.join(', ')

  const payload = {
    device: detectDeviceLabel(userAgent),
    platform,
    browser: detectBrowserName(userAgent),
    ip_address: geoInfo?.ip || '',
    city: geoInfo?.city || '',
    region: geoInfo?.region || '',
    country: geoInfo?.country || '',
    location_label: locationLabel,
    is_current: true,
  }

  return payload
}

export async function syncCurrentUserSession(userId) {
  if (!userId) return null
  const geoInfo = await resolveGeoInfo()
  const payload = buildSessionPayload(geoInfo)
  if (!payload) return null
  const recordId = getStoredRecordId(userId)
  if (recordId) {
    payload.session_id = recordId
  }
  try {
    const { data } = await http.post(USER_SESSIONS_ENDPOINT, payload)
    if (data?.id) {
      storeRecordId(userId, data.id)
      return data.id
    }
  } catch (error) {
    console.warn('No se pudo registrar la sesión activa.', error)
    throw error
  }
  return null
}

export async function clearCurrentUserSession(userId) {
  if (!userId) return
  const recordId = getStoredRecordId(userId)
  clearStoredRecordId(userId)
  if (!recordId) return
  try {
    await http.delete(`${USER_SESSIONS_ENDPOINT}/${encodeURIComponent(recordId)}`)
  } catch (error) {
    console.warn('No se pudo limpiar la sesión local.', error)
  }
}

export const getLocalSessionRecordId = userId => getStoredRecordId(userId)
